import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ActionButton,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
} from '../../common/components';
import { Form } from '../../common/form';
import { usePeriodPredefines } from './utils';
import {
  ExecutionPeriodFormDataType,
  ExecutionPeriodsEditFormType,
  ExecutionPeriodsEditType,
} from '../schema';
import { ExecutionPeriodsEditFormSchema } from '../schema/ExecutionFormDataSchema';
import translations from '../translations/ExecutionPeriods.translations.json';
import { defaultPeriod, generateId, PeriodArray } from './PeriodArray';
import { PredefineClassifiers } from './types';
import { usePeriodLoadingType } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'execution-periodicity-modal',
);
addTranslations(translations);

export interface PeriodicityModalProps {
  onCancel: () => void;
  onOk: (periods: ExecutionPeriodFormDataType[]) => void;
  isInput: boolean;
}

function initialPeriods(
  periods: ExecutionPeriodFormDataType[],
  sourceDataVersion: number | null,
) {
  let executionPeriods: ExecutionPeriodsEditType[];
  if (periods && periods.length) {
    executionPeriods = periods.map((period) => {
      const {
        minutesPredefined,
        minutesScheduled,
        hoursPredefined,
        hoursScheduled,
        daysPredefined,
        daysScheduled,
        weekdaysPredefined,
        weekdaysScheduled,
        monthsPredefined,
        monthsScheduled,
        ...rest
      } = period;
      return {
        ...rest,
        editId: period.id?.toString() ?? generateId(),
        existingPeriod: null,
        minutes: period.minutesPredefined
          ? [period.minutesPredefined]
          : (period.minutesScheduled ?? []),
        hours: period.hoursPredefined
          ? [period.hoursPredefined]
          : (period.hoursScheduled ?? []),
        days: period.daysPredefined
          ? [period.daysPredefined]
          : (period.daysScheduled ?? []),
        weekdays: period.weekdaysPredefined
          ? [period.weekdaysPredefined]
          : (period.weekdaysScheduled ?? []),
        months: period.monthsPredefined
          ? [period.monthsPredefined]
          : (period.monthsScheduled ?? []),
      };
    });
  } else {
    executionPeriods = [defaultPeriod(sourceDataVersion)];
  }
  return {
    periods: executionPeriods,
  };
}

function fillPeriods(
  values: ExecutionPeriodsEditType[],
  predefines: PredefineClassifiers,
) {
  return values.map((period) => {
    const { minutes, hours, days, weekdays, months, ...rest } = period;
    const updated: ExecutionPeriodFormDataType = {
      ...rest,
      minutesPredefined: null,
      minutesScheduled: [],
      hoursPredefined: null,
      hoursScheduled: [],
      daysPredefined: null,
      daysScheduled: [],
      weekdaysPredefined: null,
      weekdaysScheduled: [],
      monthsPredefined: null,
      monthsScheduled: [],
    };
    const minute = minutes?.[0];
    if (minute && predefines.minutes.has(minute)) {
      updated.minutesPredefined = minute;
    } else {
      updated.minutesScheduled = minutes ?? [];
    }

    const hour = hours?.[0];
    if (hour && predefines.hours.has(hour)) {
      updated.hoursPredefined = hour;
    } else {
      updated.hoursScheduled = hours ?? [];
    }

    const day = days?.[0];
    if (day && predefines.days.has(day)) {
      updated.daysPredefined = day;
    } else {
      updated.daysScheduled = days ?? [];
    }

    const weekday = weekdays?.[0];
    if (weekday && predefines.weekdays.has(weekday)) {
      updated.weekdaysPredefined = weekday;
    } else {
      updated.weekdaysScheduled = weekdays ?? [];
    }

    const month = months?.[0];
    if (month && predefines.months.has(month)) {
      updated.monthsPredefined = month;
    } else {
      updated.monthsScheduled = months ?? [];
    }

    return updated;
  });
}

export function PeriodicityModal({
  onCancel,
  onOk,
  isInput,
}: PeriodicityModalProps) {
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const { periodLoadingType } = usePeriodLoadingType();
  const predefines = usePeriodPredefines();

  const sourceDataVersionId = getValues('execution.sourceDataVersionId');
  const executionPeriods = getValues('execution.executionPeriods');

  const [periods] = useState(() =>
    initialPeriods(executionPeriods, sourceDataVersionId),
  );

  const handleFinish = useCallback(
    (values: ExecutionPeriodsEditFormType) => {
      const periods = values.periods;
      if (periodLoadingType === 'FIXED') {
        // If fixed loading period selected, add period count for new rows
        const periodCnt = getValues('execution.periodCount');
        if (typeof periodCnt === 'number') {
          periods
            .filter((period) => !period.existingPeriod)
            .forEach((period) => (period.periodCount = periodCnt));
        }
      }
      onOk(fillPeriods(periods, predefines));
    },
    [onOk, getValues, predefines, periodLoadingType],
  );

  return (
    <Form
      schema={ExecutionPeriodsEditFormSchema}
      defaultValues={periods}
      onSubmit={handleFinish}
      idAttributePrefix="executionPeriods"
    >
      <Modal
        onCancel={onCancel}
        footer={
          <ModalFooter>
            <Flex justify="end" gap="small">
              <ActionButton type="abort" onClick={onCancel} />
              <ActionButton type="formSubmit" label={t('button.ok')} />
            </Flex>
          </ModalFooter>
        }
        width={893}
      >
        <ModalHeader>
          <ModalTitle>
            {isInput
              ? t('modal.periods.title.in')
              : t('modal.periods.title.out')}
          </ModalTitle>
        </ModalHeader>
        <Padding scrollable>
          <PeriodArray sourceDataVersionId={sourceDataVersionId} />
        </Padding>
      </Modal>
    </Form>
  );
}
